(() => {
  if (customElements.get('buy-the-look-component')) {
    return;
  }

  class BuyTheLookComponent extends HTMLElement {
    constructor() {
      super();
      this.products = [];
      this.observer = null;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.style.display = '';
      this.classList.remove('hidden');
      
      const addToCartButton = this.querySelector('.buy-the-look__add-to-cart');
      if (addToCartButton) {
        addToCartButton.addEventListener('click', () => {
          this.addToCart();
        });
      }

      this.listenForVariantChanges();

      const existingProducts = this.querySelectorAll('.buy-the-look-product:not([data-current-product])');
      if (existingProducts.length > 0) {
        this.setupEventListeners();
        this.updateTotal();
        this.classList.remove('buy-the-look--loading');
        return;
      }

      this.setupAsyncLoading();
    }

    listenForVariantChanges() {
      const currentCard = this.querySelector('.buy-the-look-product[data-current-product]');
      if (!currentCard) return;

      const productInfo = document.querySelector('product-info');
      if (!productInfo) return;

      const self = this;
      let lastVariantId = currentCard.getAttribute('data-variant-id');
      let syncingFromBTL = false;

      const currentSizeSelect = currentCard.querySelector('[data-current-size-select]');

      const syncVariantFromPDP = () => {
        if (syncingFromBTL) return;

        const variantInput = productInfo.querySelector('input[name="id"]');
        if (!variantInput) return;

        const newVariantId = variantInput.value;
        if (newVariantId === lastVariantId) return;
        lastVariantId = newVariantId;

        currentCard.setAttribute('data-variant-id', newVariantId);

        if (currentSizeSelect) {
          const option = currentSizeSelect.querySelector(`option[value="${newVariantId}"]`);
          if (option) {
            currentSizeSelect.value = newVariantId;
            const checkbox = currentCard.querySelector('.buy-the-look-product__checkbox');
            if (checkbox) checkbox.setAttribute('data-product-price', option.dataset.price);
          }
        }

        const priceContainer = productInfo.querySelector('#price-' + self.getAttribute('data-section-id') + ' .price');
        if (priceContainer) {
          const priceDisplay = currentCard.querySelector('.buy-the-look-product__price');
          if (priceDisplay) priceDisplay.innerHTML = priceContainer.outerHTML;
        }

        const productHandle = productInfo.getAttribute('data-product-handle');
        if (productHandle) {
          fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}.js`)
            .then(r => r.json())
            .then(productData => {
              const variant = productData.variants.find(v => String(v.id) === String(newVariantId));
              if (variant) {
                const checkbox = currentCard.querySelector('.buy-the-look-product__checkbox');
                if (checkbox) checkbox.setAttribute('data-product-price', variant.price);
                self.updateTotal();
              }
            })
            .catch(() => {});
        }
      };

      const observer = new MutationObserver(syncVariantFromPDP);
      observer.observe(productInfo, { subtree: true, attributes: true, childList: true });

      if (currentSizeSelect) {
        currentSizeSelect.addEventListener('change', (e) => {
          const newVariantId = e.target.value;
          lastVariantId = newVariantId;
          syncingFromBTL = true;

          currentCard.setAttribute('data-variant-id', newVariantId);

          const option = e.target.options[e.target.selectedIndex];
          const newPrice = parseInt(option.getAttribute('data-price'));
          const checkbox = currentCard.querySelector('.buy-the-look-product__checkbox');
          if (checkbox) checkbox.setAttribute('data-product-price', newPrice);

          const variantInput = productInfo.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.value = newVariantId;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          const sizeRadio = productInfo.querySelector(`input[type="radio"][value="${newVariantId}"]`);
          if (sizeRadio) {
            sizeRadio.click();
          } else {
            const productHandle = productInfo.getAttribute('data-product-handle');
            if (productHandle) {
              fetch(`${window.Shopify?.routes?.root || '/'}products/${productHandle}.js`)
                .then(r => r.json())
                .then(productData => {
                  const variant = productData.variants.find(v => String(v.id) === String(newVariantId));
                  if (variant && variant.option2) {
                    const sizeLabels = productInfo.querySelectorAll('.product__option-values label, .variant-picker__options label');
                    sizeLabels.forEach(label => {
                      const text = label.textContent.trim();
                      if (text === variant.option2 || text === variant.option1) {
                        const radio = label.previousElementSibling;
                        if (radio && radio.type === 'radio' && !radio.checked) {
                          radio.click();
                        }
                      }
                    });
                  }
                })
                .catch(() => {});
            }
          }

          self.updateTotal();
          setTimeout(() => { syncingFromBTL = false; }, 200);
        });
      }
    }

    setupAsyncLoading() {
      const url = this.getAttribute('data-url');
      if (!url) {
        console.warn('Buy The Look: No URL provided for loading products');
        this.classList.remove('buy-the-look--loading');
        return;
      }

      this.loadProducts(url);
      
      const handleIntersection = (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        
        const existingProducts = this.querySelectorAll('.buy-the-look-product');
        if (existingProducts.length > 0) {
          observer.unobserve(this);
          return;
        }
        
        observer.unobserve(this);
        this.loadProducts(url);
      };

      this.observer = new IntersectionObserver(handleIntersection, {
        rootMargin: '0px 0px 200px 0px'
      });
      
      this.observer.observe(this);
    }

    buildJsonUrl(dataUrl, productId) {
      try {
        const urlObj = new URL(dataUrl, window.location.origin);
        const params = new URLSearchParams(urlObj.search);
        
        const pathParts = urlObj.pathname.split('/');
        const basePath = pathParts.slice(0, -1).join('/') || '';
        
        const routesRoot = window.Shopify?.routes?.root || '/';
        const jsonUrl = `${routesRoot}recommendations/products.json?product_id=${productId}&limit=${params.get('limit') || '10'}&intent=${params.get('intent') || 'complementary'}`;
        
        return jsonUrl;
      } catch (e) {
        console.warn('Buy The Look: Error parsing data-url, using fallback:', e);
        const routesRoot = window.Shopify?.routes?.root || '/';
        return `${routesRoot}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`;
      }
    }

    async loadProducts(url) {
      try {
        const sectionId = this.getAttribute('data-section-id');
        const productId = this.getAttribute('data-product-id');
        const productsContainer = this.querySelector('.buy-the-look__products');
        
        if (!productsContainer) {
          console.warn('Buy The Look: Products container not found');
          this.classList.remove('buy-the-look--loading');
          return;
        }
        
        if (!productId) {
          console.warn('Buy The Look: No product ID provided');
          this.classList.remove('buy-the-look--loading');
          return;
        }
        
        console.log('Buy The Look: Loading complementary products for product:', productId);
        
        const dataUrl = this.getAttribute('data-url');
        const recommendationsUrl = dataUrl ? this.buildJsonUrl(dataUrl, productId) : 
          `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`;
        
        console.log('Buy The Look: Fetching from URL:', recommendationsUrl);
        const response = await fetch(recommendationsUrl);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Buy The Look: API error response:', response.status, errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Buy The Look: API response:', data);
        
        if (data.status && data.status !== 200) {
          console.error('Buy The Look: API returned error:', data.message || data.description);
          this.classList.remove('buy-the-look--loading');
          return;
        }
        
        if (data.products && data.products.length > 0) {
          console.log(`Buy The Look: Found ${data.products.length} complementary products`);
          
          const productHTMLs = data.products.map((product, index) => 
            this.renderProductHTML(product, sectionId, index)
          ).filter(Boolean);
          
          if (productHTMLs.length > 0) {
            const currentProductEl = productsContainer.querySelector('.buy-the-look-product[data-current-product]');
            if (currentProductEl) {
              currentProductEl.insertAdjacentHTML('afterend', productHTMLs.join(''));
            } else {
              productsContainer.innerHTML = productHTMLs.join('');
            }
            this.setupEventListeners();
            this.updateTotal();
            this.classList.remove('buy-the-look--loading');
            const parentContainer = this.closest('.section-complete-the-set__content');
            if (parentContainer) {
              parentContainer.classList.remove('hidden');
            }
            console.log(`Buy The Look: Successfully rendered ${productHTMLs.length} products`);
            return;
          }
        }
        
        console.warn('Buy The Look: No complementary products found in response. Response data:', data);
        this.classList.remove('buy-the-look--loading');
        const inlineWrapper = this.closest('.product__buy-the-look');
        const sectionWrapper = this.closest('.section-complete-the-set__content');
        if (inlineWrapper) {
          inlineWrapper.style.display = 'none';
        } else if (sectionWrapper) {
          sectionWrapper.style.display = 'none';
        } else {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error loading complementary products:', error);
        this.classList.remove('buy-the-look--loading');
      }
    }
    
    renderProductHTML(product, sectionId, index) {
      if (!product || !product.id) return '';
      
      const availableVariants = product.variants ? product.variants.filter(v => v.available) : [];
      const variant = availableVariants.length > 0 ? availableVariants[0] : (product.variants && product.variants.length > 0 ? product.variants[0] : null);
      if (!variant) return '';
      
      const productId = `buy-the-look-${sectionId}-${index}`;
      const price = variant.price || 0;
      
      let imageUrl = '';
      if (product.featured_image) {
        imageUrl = typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image.src || '');
      } else if (product.images && product.images.length > 0) {
        const firstImage = product.images[0];
        imageUrl = typeof firstImage === 'string' ? firstImage : (firstImage.src || '');
      }
      
      let sizeOptions = '';
      if (product.options && product.options.length > 0) {
        const sizeOption = product.options.find(opt => opt && opt.name && opt.name.toLowerCase().includes('size')) || product.options[0];
        if (sizeOption && product.variants) {
          const sizeOptionIndex = product.options.indexOf(sizeOption);
          const variantsForSize = availableVariants.length > 0 ? availableVariants : product.variants;
          sizeOptions = variantsForSize.map(v => {
            const size = v.option1 && sizeOptionIndex === 0 ? v.option1 : 
                        (v.option2 && sizeOptionIndex === 1 ? v.option2 : 
                        (v.option3 && sizeOptionIndex === 2 ? v.option3 : ''));
            return `<option value="${v.id}" data-price="${v.price}" ${v.id === variant.id ? 'selected' : ''}>${size}</option>`;
          }).join('');
        }
      }
      
      const title = (product.title || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      
      return `
        <div class="buy-the-look-product" data-product-id="${product.id}" data-variant-id="${variant.id}">
          <div class="buy-the-look-product__checkbox-wrapper">
            <div class="checkbox">
              <input 
                type="checkbox" 
                id="${productId}-checkbox"
                class="buy-the-look-product__checkbox"
                data-product-price="${price}"
              >
              <label for="${productId}-checkbox" class="visually-hidden">
                Select ${title}
              </label>
            </div>
          </div>
          <div class="buy-the-look-product__image">
            ${imageUrl ? `<img src="${imageUrl}" alt="${title}" loading="lazy" width="200" height="200">` : '<div class="placeholder-svg">Product Image</div>'}
          </div>
          <div class="buy-the-look-product__details">
            <h3 class="buy-the-look-product__title">${title}</h3>
            <div class="buy-the-look-product__price">
              <span class="price">${this.formatMoney(price)}</span>
            </div>
            ${sizeOptions ? `
              <div class="buy-the-look-product__size-selector">
                <label for="${productId}-size" class="visually-hidden">Size</label>
                <select id="${productId}-size" class="buy-the-look-product__size-select" data-product-id="${product.id}">
                  ${sizeOptions}
                </select>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }

    setupEventListeners() {
      const checkboxes = this.querySelectorAll('.buy-the-look-product__checkbox');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateTotal();
          this.updateAddToCartButton();
        });
      });

      const sizeSelects = this.querySelectorAll('.buy-the-look-product__size-select');
      sizeSelects.forEach(select => {
        select.addEventListener('change', (e) => {
          const option = e.target.options[e.target.selectedIndex];
          const newPrice = parseInt(option.getAttribute('data-price'));
          const productCard = e.target.closest('.buy-the-look-product');
          const checkbox = productCard.querySelector('.buy-the-look-product__checkbox');
          
          productCard.setAttribute('data-variant-id', option.value);
          checkbox.setAttribute('data-product-price', newPrice);
          
          this.updateTotal();
        });
      });
    }

    updateTotal() {
      const checkedProducts = this.querySelectorAll('.buy-the-look-product__checkbox:checked');
      let total = 0;

      checkedProducts.forEach(checkbox => {
        const price = parseInt(checkbox.getAttribute('data-product-price')) || 0;
        total += price;
      });

      const totalElement = this.querySelector('.buy-the-look__total-price');
      if (totalElement) {
        totalElement.setAttribute('data-total-price', total);
        totalElement.innerHTML = this.formatMoney(total);
      }

      this.updateAddToCartButton();
    }

    updateAddToCartButton() {
      const checkedProducts = this.querySelectorAll('.buy-the-look-product__checkbox:checked');
      const addToCartButton = this.querySelector('.buy-the-look__add-to-cart');
      
      if (addToCartButton) {
        addToCartButton.disabled = checkedProducts.length === 0;
      }
    }

    // Same display as the rest of the storefront: "1,200 AED" (global.js
    // formatPrice / snippets/price-display.liquid).
    formatMoney(cents) {
      if (typeof formatPrice === 'function') return formatPrice('', cents / 100);
      const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
      const units = Math.ceil(cents / 100);
      return `${units.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${currency}`.trim();
    }

    async addToCart() {
      const checkedProducts = this.querySelectorAll('.buy-the-look-product__checkbox:checked');
      if (checkedProducts.length === 0) return;

      const addToCartButton = this.querySelector('.buy-the-look__add-to-cart');
      const originalText = addToCartButton.textContent;
      
      addToCartButton.disabled = true;
      addToCartButton.textContent = 'Adding...';

      const items = [];
      checkedProducts.forEach(checkbox => {
        const productCard = checkbox.closest('.buy-the-look-product');
        const variantId = productCard.getAttribute('data-variant-id');
        if (variantId) {
          const variantIdInt = parseInt(variantId, 10);
          if (!isNaN(variantIdInt)) {
            items.push({
              id: variantIdInt,
              quantity: 1
            });
          }
        }
      });

      if (items.length === 0) {
        addToCartButton.disabled = false;
        addToCartButton.textContent = originalText;
        return;
      }

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        
        const routes = window.routes || (typeof routes !== 'undefined' ? routes : {});
        const cartAddUrl = routes.cart_add_url || '/cart/add.js';
        
        const formData = new FormData();
        if (items.length === 1) {
          formData.append('id', items[0].id);
          formData.append('quantity', items[0].quantity);
        } else {
          items.forEach((item, index) => {
            formData.append(`items[${index}][id]`, item.id);
            formData.append(`items[${index}][quantity]`, item.quantity);
          });
        }
        
        if (cartDrawer && cartDrawer.getSectionsToRender) {
          const sections = cartDrawer.getSectionsToRender().map(section => section.section);
          if (sections.length > 0) {
            formData.append('sections', sections.join(','));
          }
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);
        }
        
        let config;
        if (typeof fetchConfig === 'function') {
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
        } else {
          config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          };
        }
        config.body = formData;

        const response = await fetch(cartAddUrl, config);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || errorData.description || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.status || data.errors) {
          const errorMessage = data.message || data.description || data.errors || 'Failed to add to cart';
          throw new Error(errorMessage);
        }
        
        if (cartDrawer && cartDrawer.renderContents) {
          if (data.sections) {
            cartDrawer.renderContents(data, true);
          } else {
            cartDrawer.renderContents(data, true);
          }
        } else {
          if (window.dispatchEvent) {
            window.dispatchEvent(new CustomEvent('cart:update', { detail: data }));
          }
          this.updateCartCount();
        }
        
        addToCartButton.textContent = 'Added to cart!';
        addToCartButton.style.background = '#000000';
        addToCartButton.style.color = '#ffffff';
        
        setTimeout(() => {
          addToCartButton.textContent = originalText;
          addToCartButton.disabled = false;
          this.updateAddToCartButton();
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        addToCartButton.textContent = 'Error - Try Again';
        addToCartButton.style.background = '#000000';
        addToCartButton.style.color = '#ffffff';
        setTimeout(() => {
          addToCartButton.textContent = originalText;
          addToCartButton.disabled = false;
          this.updateAddToCartButton();
        }, 3000);
      }
    }

    updateCartCount() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCounters = document.querySelectorAll('.cart-count, [data-cart-count]');
          cartCounters.forEach(counter => {
            counter.textContent = cart.item_count;
          });
        })
        .catch(error => {
          console.error('Error updating cart count:', error);
        });
    }
  }

  customElements.define('buy-the-look-component', BuyTheLookComponent);
})();
